import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Upload, Save, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";

const num = (v) =>
  v === "" || v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`;

const PAGE_SIZE = 50;

export default function ShippingBulkPanel() {
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPrice, setBulkPrice] = useState("4.99");
  const [saving, setSaving] = useState(false);
  const [clearingSelected, setClearingSelected] = useState(false);
  const [applyingToAll, setApplyingToAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const fileRef = useRef(null);

  // Global shipping policy
  const [policy, setPolicy] = useState(null);
  const [policySaving, setPolicySaving] = useState(false);

  // How many listings currently override Global (catalogue-wide, ignores the
  // table's search filter) — the only way to tell "nothing changed" apart from
  // "nothing needed changing" after a clear.
  const [overrideStats, setOverrideStats] = useState(null);

  const loadOverrideStats = useCallback(async () => {
    try {
      const r = await api.get("/admin/shipping/override-stats");
      setOverrideStats(r.data);
    } catch {
      setOverrideStats(null);
    }
  }, []);

  useEffect(() => { loadOverrideStats(); }, [loadOverrideStats]);

  const loadPolicy = useCallback(async () => {
    try {
      const r = await api.get("/shipping/settings");
      setPolicy(r.data);
    } catch {
      toast.error("Could not load global shipping policy");
    }
  }, []);

  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const savePolicy = async () => {
    if (!policy) return;
    setPolicySaving(true);
    try {
      const r = await api.put("/admin/shipping/settings", {
        handling_days: Number(policy.handling_days),
        delivery_min_days: Number(policy.delivery_min_days),
        delivery_max_days: Number(policy.delivery_max_days),
        base_price: Number(policy.base_price),
        mode: policy.mode,
      });
      setPolicy(r.data);
      toast.success("Global shipping policy saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save policy");
    } finally {
      setPolicySaving(false);
    }
  };

  // Debounce the free-text filter before it hits the server.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Searching resets back to page 1 so results aren't stranded past the new total.
  useEffect(() => { setPage(1); }, [debouncedQ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (debouncedQ) params.q = debouncedQ;
      const r = await api.get("/books", { params });
      const data = r.data;
      const items = Array.isArray(data) ? data : (data.items || []);
      setBooks(items);
      setTotal(Array.isArray(data) ? items.length : (data.total ?? items.length));
      setTotalPages(Array.isArray(data) ? 1 : (data.total_pages ?? 1));
    } catch {
      toast.error("Could not load listings");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  const allChecked = books.length > 0 && books.every((b) => selected.has(b.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) books.forEach((b) => next.delete(b.id));
    else books.forEach((b) => next.add(b.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const applyBulk = async () => {
    if (selected.size === 0) return toast.error("Select at least one listing");
    const price = Number(bulkPrice);
    if (Number.isNaN(price) || price < 0) return toast.error("Enter a valid price ≥ 0");
    setSaving(true);
    try {
      const r = await api.post("/admin/shipping/bulk-set", {
        ids: Array.from(selected),
        shipping_price: price,
      });
      toast.success(`Updated ${r.data.modified} of ${r.data.matched} listings`);
      setSelected(new Set());
      await Promise.all([load(), loadOverrideStats()]);
    } catch {
      toast.error("Bulk update failed");
    } finally {
      setSaving(false);
    }
  };

  // A clear is a success when no targeted listing is left holding an override —
  // including the case where none had one to begin with. Reporting only
  // "modified" made an already-clean catalogue look like a silent failure.
  const clearResultMessage = (d, scopeLabel) => {
    const inScope = d.in_scope ?? d.matched ?? 0;
    if (d.remaining_overrides > 0) {
      return {
        ok: false,
        text: `${d.cleared} cleared, but ${d.remaining_overrides.toLocaleString()} ${scopeLabel} still override Global`,
      };
    }
    if (d.cleared > 0) {
      return {
        ok: true,
        text: `Cleared the override on ${d.cleared.toLocaleString()} listing${d.cleared === 1 ? "" : "s"} — all ${inScope.toLocaleString()} ${scopeLabel} now follow Global`,
      };
    }
    return {
      ok: true,
      text: `No change needed — all ${inScope.toLocaleString()} ${scopeLabel} already follow the Global policy`,
    };
  };

  const clearSelected = async () => {
    if (selected.size === 0) return toast.error("Select at least one listing");
    setClearingSelected(true);
    try {
      const r = await api.post("/admin/shipping/bulk-clear", {
        ids: Array.from(selected),
      });
      const msg = clearResultMessage(r.data, "selected listings");
      msg.ok ? toast.success(msg.text) : toast.error(msg.text);
      setSelected(new Set());
      await Promise.all([load(), loadOverrideStats()]);
    } catch {
      toast.error("Clear failed");
    } finally {
      setClearingSelected(false);
    }
  };

  const applyGlobalToAll = async () => {
    if (!window.confirm(
      "This clears the shipping/handling/delivery override on EVERY listing in the catalogue " +
      "so they all follow the Global policy above.\n\nThis cannot be undone from here — export a " +
      "backup first if you need to restore individual overrides.\n\nContinue?"
    )) return;
    setApplyingToAll(true);
    try {
      const r = await api.post("/admin/shipping/bulk-clear", { apply_to_all: true });
      const msg = clearResultMessage(r.data, "listings");
      msg.ok ? toast.success(msg.text) : toast.error(msg.text);
      setSelected(new Set());
      await Promise.all([load(), loadOverrideStats()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apply Global to All failed");
    } finally {
      setApplyingToAll(false);
    }
  };

  const downloadFile = async (path, name) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
  };

  const onUploadCsv = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", f);
    try {
      const r = await api.post("/admin/shipping/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { updated, error_count } = r.data;
      toast.success(`Imported: ${updated} updated${error_count ? `, ${error_count} rows skipped` : ""}`);
      await Promise.all([load(), loadOverrideStats()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-8 space-y-6 max-w-6xl" data-testid="shipping-panel">
      <header className="border-b border-[#E5E1D8] pb-4">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Fulfilment</p>
        <h2 className="font-serif text-3xl mt-1">Shipping prices</h2>
        <p className="text-sm text-[#1A1714]/70 mt-2 max-w-2xl">
          Set a shipping price per listing. Use bulk edit to apply the same
          amount to multiple listings, or upload a CSV to update prices in bulk.
        </p>
      </header>

      {/* Global shipping policy */}
      <section className="border border-[#E5E1D8] p-6 space-y-4" data-testid="shipping-global-policy">
        <div>
          <h3 className="font-serif text-xl">Global shipping policy</h3>
          <p className="text-xs text-[#1A1714]/60 mt-1 max-w-2xl">
            These defaults apply to every listing that has no override. A listing's own override
            (edit dialog, bulk edit, or CSV) always wins — clear it to fall back to Global.
          </p>
        </div>
        {policy ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-1">
                  Handling time (days)
                </label>
                <Input
                  type="number" min="0" max="60"
                  value={policy.handling_days}
                  onChange={(e) => setPolicy({ ...policy, handling_days: e.target.value })}
                  className="rounded-none h-10"
                  data-testid="shipping-global-handling"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-1">
                  Delivery min (days)
                </label>
                <Input
                  type="number" min="0" max="60"
                  value={policy.delivery_min_days}
                  onChange={(e) => setPolicy({ ...policy, delivery_min_days: e.target.value })}
                  className="rounded-none h-10"
                  data-testid="shipping-global-delivery-min"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-1">
                  Delivery max (days)
                </label>
                <Input
                  type="number" min="0" max="90"
                  value={policy.delivery_max_days}
                  onChange={(e) => setPolicy({ ...policy, delivery_max_days: e.target.value })}
                  className="rounded-none h-10"
                  data-testid="shipping-global-delivery-max"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-1">
                  Default price (USD)
                </label>
                <Input
                  type="number" step="0.01" min="0"
                  value={policy.base_price}
                  onChange={(e) => setPolicy({ ...policy, base_price: e.target.value })}
                  className="rounded-none h-10"
                  data-testid="shipping-global-price"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-4 pt-3 border-t border-[#E5E1D8]/50">
              <div className="min-w-[280px]">
                <label className="block font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-1">
                  Checkout calculation
                </label>
                <select
                  value={policy.mode}
                  onChange={(e) => setPolicy({ ...policy, mode: e.target.value })}
                  className="w-full h-10 border border-[#E5E1D8] bg-transparent px-3 text-sm rounded-none"
                  data-testid="shipping-global-mode"
                >
                  <option value="per_item">Per book — each listing's shipping adds up across the cart</option>
                  <option value="flat">Flat per order — one default rate, plus any per-book overrides</option>
                </select>
              </div>
              <Button
                onClick={savePolicy}
                disabled={policySaving}
                className="rounded-none bg-[#1A1714] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
                data-testid="shipping-global-save"
              >
                <Save size={12} className="mr-1" />
                {policySaving ? "Saving…" : "Save policy"}
              </Button>
              {policy.updated_at && (
                <p className="text-[11px] text-[#1A1714]/40" data-testid="shipping-global-updated">
                  Last saved {new Date(policy.updated_at).toLocaleString()}
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-[#1A1714]/60 italic">Loading policy…</p>
        )}
      </section>

      {/* Apply Global to All Listings */}
      <section className="border border-[#7A2021]/30 bg-[#7A2021]/5 p-6 space-y-3" data-testid="shipping-apply-all-section">
        <div>
          <h3 className="font-serif text-xl">Apply Global to all listings</h3>
          <p className="text-xs text-[#1A1714]/60 mt-1 max-w-2xl">
            Clears the shipping/handling/delivery override on every listing in the catalogue —
            the whole catalogue, not just the rows matching the search below — so they all follow
            the Global policy above and stay in sync with future Global changes. This does not
            delete or rewrite any other field.
          </p>
          {overrideStats && (
            <p className="text-xs mt-2 font-medium" data-testid="shipping-override-stats">
              {overrideStats.overridden > 0 ? (
                <span className="text-[#7A2021]">
                  {overrideStats.overridden.toLocaleString()} of{" "}
                  {overrideStats.total.toLocaleString()} listings currently override Global.
                </span>
              ) : (
                <span className="text-[#1A1714]/60">
                  All {overrideStats.total.toLocaleString()} listings already follow the Global
                  policy — nothing to clear.
                </span>
              )}
            </p>
          )}
        </div>
        <Button
          onClick={applyGlobalToAll}
          disabled={applyingToAll}
          variant="outline"
          className="rounded-none border-[#7A2021] text-[#7A2021] hover:bg-[#7A2021] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
          data-testid="shipping-apply-global-all"
        >
          <RotateCcw size={12} className="mr-1" />
          {applyingToAll ? "Applying…" : "Apply Global to All Listings"}
        </Button>
      </section>

      {/* Bulk edit + CSV controls */}
      <section className="border border-[#E5E1D8] p-6 space-y-4" data-testid="shipping-bulk-controls">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <label className="block font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-1">
              Shipping price (USD)
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              className="rounded-none h-10"
              data-testid="shipping-bulk-price"
            />
            <p className="text-[11px] text-[#1A1714]/50 mt-1">Enter 0.00 for free shipping.</p>
          </div>
          <Button
            onClick={applyBulk}
            disabled={saving || selected.size === 0}
            className="rounded-none bg-[#1A1714] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            data-testid="shipping-apply-bulk"
          >
            <Save size={12} className="mr-1" />
            Apply to {selected.size} selected
          </Button>
          <Button
            onClick={clearSelected}
            disabled={clearingSelected || selected.size === 0}
            variant="outline"
            className="rounded-none border-[#E5E1D8] font-mono text-xs uppercase tracking-editorial"
            data-testid="shipping-clear-bulk"
          >
            <RotateCcw size={12} className="mr-1" />
            Clear override on {selected.size} selected
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E5E1D8]/50">
          <Button
            onClick={() => downloadFile("/admin/shipping/template.csv", "shipping-policy-template.csv")}
            variant="outline"
            className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
            data-testid="shipping-template-btn"
          >
            <Download size={12} className="mr-1" /> Template CSV
          </Button>
          <Button
            onClick={() => downloadFile("/admin/shipping/export.csv", "shipping-prices.csv")}
            variant="outline"
            className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
            data-testid="shipping-export-btn"
          >
            <Download size={12} className="mr-1" /> Export all
          </Button>
          <label className="inline-flex">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onUploadCsv}
              data-testid="shipping-upload-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
              data-testid="shipping-upload-btn"
            >
              <Upload size={12} className="mr-1" />
              {uploading ? "Uploading…" : "Upload CSV"}
            </Button>
          </label>
        </div>
        <p className="text-[11px] text-[#1A1714]/50">
          CSV Shipping Price column: a number sets an override, the word <code>GLOBAL</code> clears
          the override back to the Global policy, and a blank cell leaves that listing untouched.
          Only rows present in the file are affected.
        </p>
      </section>

      {/* Listings table */}
      <section className="border border-[#E5E1D8] p-6 space-y-3" data-testid="shipping-listings">
        <div className="flex items-center justify-between gap-3">
          <Input
            placeholder="Search by title, author, SKU, or ISBN across the full catalogue…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-none h-10 max-w-md"
            data-testid="shipping-filter"
          />
          <p className="text-xs text-[#1A1714]/60">
            {selected.size} selected · page {page} of {totalPages} · {total.toLocaleString()} listings total
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-[#1A1714]/60 italic">Loading listings…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 border-b border-[#E5E1D8]">
                  <th className="py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      data-testid="shipping-select-all"
                    />
                  </th>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">SKU</th>
                  <th className="py-2 pr-3">ISBN</th>
                  <th className="py-2 pr-3 text-right">Shipping</th>
                </tr>
              </thead>
              <tbody>
                {books.map((b) => (
                  <tr key={b.id} className="border-b border-[#E5E1D8]/60" data-testid={`shipping-row-${b.id}`}>
                    <td className="py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(b.id)}
                        onChange={() => toggleOne(b.id)}
                        data-testid={`shipping-select-${b.id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-medium truncate max-w-[400px]">{b.title}</div>
                      <div className="text-xs text-[#1A1714]/60">{b.author}</div>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{b.sku || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{b.isbn_13 || b.isbn || "—"}</td>
                    <td className="py-2 pr-3 text-right">
                      {b.shipping_price != null ? (
                        <span className="font-medium" data-testid={`shipping-override-${b.id}`}>
                          Override ({num(b.shipping_price)})
                        </span>
                      ) : (
                        <span className="text-[#1A1714]/45" data-testid={`shipping-default-${b.id}`}>
                          {policy ? `Global (${num(policy.base_price)})` : "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {books.length === 0 && (
              <p className="text-sm text-[#1A1714]/60 italic py-6 text-center">No listings match your search.</p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-[#E5E1D8]/50">
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            variant="outline"
            className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
            data-testid="shipping-page-prev"
          >
            <ChevronLeft size={12} className="mr-1" /> Prev
          </Button>
          <p className="text-xs text-[#1A1714]/60">Page {page} of {totalPages}</p>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            variant="outline"
            className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
            data-testid="shipping-page-next"
          >
            Next <ChevronRight size={12} className="ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
}
